/** Mirror of backend/src/config/planLimits.js for the pricing UI — two-track model. */

export const TRACKS = { COUPLE: 'couple', STUDIO: 'studio' };

/** Within-track ordering (for "current plan" detection). */
export const PLAN_RANK = {
  trial: 0, couple_1m: 1, couple_3m: 2, couple_6m: 3,
  wedding_starter: 1, wedding: 2, wedding_plus: 3,
  studio: 1, studio_pro: 2, studio_unlimited: 3,
  // legacy
  free: 0, basic: 1, starter: 1, pro: 2, elite: 3,
};

/** A plan is purchasable from the pricing page unless it's the user's current plan. */
export function isPlanUpgrade(currentPlanId, targetPlanId) {
  if (!targetPlanId || targetPlanId === currentPlanId) return false;
  return true;
}

/** Labels for legacy plan IDs that predate the current plan catalogue. */
export const LEGACY_PLANS = [
  { id: 'free',    label: 'Free' },
  { id: 'basic',   label: 'Basic' },
  { id: 'starter', label: 'Starter' },
  { id: 'pro',     label: 'Pro' },
  { id: 'elite',   label: 'Elite' },
];

/** Returns a display label for any plan ID, including legacy ones. */
export function getPlanLabel(planId) {
  const current = PLANS.find((p) => p.id === planId);
  if (current) return current.label;
  const legacy = LEGACY_PLANS.find((p) => p.id === planId);
  if (legacy) return legacy.label;
  // last-resort: title-case the raw id
  return planId ? planId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';
}

export const PLANS = [
  // ─── Track A — Couples ─────────────────────────────────────────────────────
  {
    id: 'trial',
    track: TRACKS.COUPLE,
    billingType: 'free',
    hidden: true,
    label: 'Trial',
    priceOnce: 0,
    trialDays: 14,
    storageGB: 0.5,
    weddings: 1,
    guests: 50,
    photosPerGallery: 50,
    whiteLabel: false,
    features: [
      'pricing.feat.websiteRsvp',
      'pricing.feat.gallery50',
      'pricing.feat.brandingBonvow',
    ],
  },
  // Duration-based one-time plans — same features, capacity grows with the term.
  // Live Moments is the separate add-on (MOMENTS_ADDON below).
  {
    id: 'couple_1m',
    track: TRACKS.COUPLE,
    billingType: 'oneTime',
    label: '1 Month',
    labelKey: 'pricing.plan1m',
    priceOnce: 19,
    months: 1,
    storageGB: 5,
    weddings: 1,
    guests: 150,
    photosPerGallery: 500,
    whiteLabel: false,
    features: [
      'pricing.feat.photos500',
      'pricing.feat.guests150',
      'pricing.feat.storage5gb',
      'pricing.feat.downloadAll',
    ],
  },
  {
    id: 'couple_3m',
    track: TRACKS.COUPLE,
    billingType: 'oneTime',
    label: '3 Months',
    labelKey: 'pricing.plan3m',
    priceOnce: 39,
    months: 3,
    popular: true,
    storageGB: 20,
    weddings: 1,
    guests: 300,
    photosPerGallery: 2000,
    whiteLabel: false,
    features: [
      'pricing.feat.photos2000',
      'pricing.feat.guests300',
      'pricing.feat.storage20gb',
      'pricing.feat.downloadAll',
    ],
  },
  {
    id: 'couple_6m',
    track: TRACKS.COUPLE,
    billingType: 'oneTime',
    label: '6 Months',
    labelKey: 'pricing.plan6m',
    priceOnce: 59,
    months: 6,
    storageGB: 50,
    weddings: 1,
    guests: 500,
    photosPerGallery: 5000,
    whiteLabel: false,
    features: [
      'pricing.feat.photos5000',
      'pricing.feat.guests500',
      'pricing.feat.storage50gb',
      'pricing.feat.noBranding',
      'pricing.feat.downloadAll',
    ],
  },

  // Previous couples plans — no longer sold; kept so existing buyers' plan
  // names still render (dashboard badge, labels).
  {
    id: 'wedding_starter',
    track: TRACKS.COUPLE,
    billingType: 'oneTime',
    hidden: true,
    legacy: true,
    label: 'Wedding Starter',
    priceOnce: 29,
    storageGB: 2,
    weddings: 1,
    guests: 150,
    photosPerGallery: 300,
    galleryMonths: 3,
    whiteLabel: false,
    features: [
      'pricing.feat.gallery300',
      'pricing.feat.guests150',
      'pricing.feat.guestMomentsPhoto',
      'pricing.feat.noBranding',
      'pricing.feat.online3months',
    ],
  },
  {
    id: 'wedding',
    track: TRACKS.COUPLE,
    billingType: 'oneTime',
    hidden: true,
    legacy: true,
    label: 'Wedding',
    priceOnce: 49,
    storageGB: 25,
    weddings: 1,
    guests: 500,
    photosPerGallery: 3000,
    whiteLabel: false,
    features: [
      'pricing.feat.everythingStarter',
      'pricing.feat.gallery3000',
      'pricing.feat.guestMomentsVideo',
      'pricing.feat.liveSlideshow',
      'pricing.feat.online1year',
    ],
  },
  {
    id: 'wedding_plus',
    track: TRACKS.COUPLE,
    billingType: 'oneTime',
    hidden: true,
    legacy: true,
    label: 'Wedding Plus',
    priceOnce: 89,
    storageGB: 100,
    weddings: 1,
    guests: 1000,
    photosPerGallery: 'Unlimited',
    whiteLabel: false,
    features: [
      'pricing.feat.everythingWedding',
      'pricing.feat.voiceMoments',
      'pricing.feat.unlimitedPhotos',
      'pricing.feat.online1year',
      'pricing.feat.downloadAnytime',
    ],
  },

  // ─── Track B — Studios (recurring) ─────────────────────────────────────────
  {
    id: 'studio',
    track: TRACKS.STUDIO,
    billingType: 'recurring',
    label: 'Studio',
    priceMonthly: 19,
    priceAnnual: Number((19 * 12 * 0.8).toFixed(2)),
    storageGB: 250,
    weddings: 15,
    guests: 2500,
    photosPerGallery: 20000,
    whiteLabel: false,
    features: [
      'pricing.feat.galleries15',
      'pricing.feat.galleryStats',
      'pricing.feat.guestMomentsVideo',
    ],
  },
  {
    id: 'studio_pro',
    track: TRACKS.STUDIO,
    billingType: 'recurring',
    label: 'Studio Pro',
    priceMonthly: 29,
    priceAnnual: Number((29 * 12 * 0.8).toFixed(2)),
    popular: true,
    storageGB: 1000,
    weddings: 50,
    guests: 'Unlimited',
    photosPerGallery: 100000,
    whiteLabel: true,
    features: [
      'pricing.feat.galleries50',
      'pricing.feat.clientSelection',
      'pricing.feat.voiceMoments',
      'pricing.feat.storage1tb',
    ],
  },
  {
    id: 'studio_unlimited',
    track: TRACKS.STUDIO,
    billingType: 'recurring',
    label: 'Studio Unlimited',
    priceMonthly: 49,
    priceAnnual: Number((49 * 12 * 0.8).toFixed(2)),
    storageGB: 2000,
    weddings: 'Unlimited',
    guests: 'Unlimited',
    photosPerGallery: 'Unlimited',
    whiteLabel: true,
    features: [
      'pricing.feat.unlimitedGalleries',
      'pricing.feat.teamSeats',
      'pricing.feat.prioritySupport',
      'pricing.feat.storage2tb',
    ],
  },
];

export function plansForTrack(track) {
  return PLANS.filter((p) => p.track === track && !p.hidden);
}

/**
 * Live Moments add-on per couples plan — guest photo/video/voice uploads and
 * the live slideshow. Mirror of MOMENTS_ADDON in backend/src/config/planLimits.js.
 */
export const MOMENTS_ADDON = {
  couple_1m: { priceOnce: 9, storageGB: 5 },
  couple_3m: { priceOnce: 15, storageGB: 10 },
  couple_6m: { priceOnce: 19, storageGB: 20 },
};

export function formatMoneyUSD(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  const hasCents = Math.round(n * 100) % 100 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
}

export function planBadgeClass(plan) {
  switch (plan) {
    case 'trial':
    case 'free':
      return 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-950/50 dark:text-teal-200 dark:border-teal-700';
    case 'couple_1m':
    case 'wedding_starter':
      return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950/50 dark:text-orange-200 dark:border-orange-700';
    case 'couple_3m':
    case 'couple_6m':
    case 'wedding':
    case 'wedding_plus':
      return 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/50 dark:text-rose-200 dark:border-rose-700';
    case 'studio':
      return 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-950/50 dark:text-sky-200 dark:border-sky-700';
    case 'studio_pro':
      return 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-950/50 dark:text-violet-200 dark:border-violet-700';
    case 'studio_unlimited':
    case 'elite':
      return 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-700';
    case 'pro':
      return 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-950/50 dark:text-violet-200 dark:border-violet-700';
    case 'starter':
    case 'basic':
      return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950/50 dark:text-orange-200 dark:border-orange-700';
    default:
      return 'bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-600';
  }
}
